import { GameRoundPhase, type IGameRoundState } from '@hit-game/shared/types';
import { Dices, Hash, Plane, Rocket, Timer } from 'lucide-react';
import { useEffect, useState } from 'react';

import { isOddFresh } from '../../lib/rounds-packet';
import { formatMoney } from '../../utils/format';

/**
 * Nhịp vẽ lại. 60ms ≈ 16 khung/giây — giữ cho ĐỒNG HỒ đếm ngược của Tài Xỉu/Xóc
 * Đĩa Live chạy mượt (server chỉ gửi mốc đóng cược, web tự trừ tại chỗ), và rẻ vì
 * chỉ là vài phép trừ trên bốn thẻ, không hỏi server lần nào.
 *
 * KHÔNG còn liên quan tới hệ số Máy bay: hệ số bây giờ lấy thẳng số nhà cái gửi,
 * vẽ lại dày hơn cũng không làm nó đổi.
 */
const TICK_MS = 60;

/**
 * Gói ván cũ quá ngần này thì thôi vẽ "đang bay".
 *
 * GIỮ lại dù đã bỏ ngoại suy — vì nó chặn một lỗi khác: server hạ cờ "đang bay"
 * bằng cách GHI một gói mới (`AVIATOR_TICK_STALE_MS`), nên khi chính đường đẩy
 * gói chết (observer kẹt, mạng rớt, tab ngủ) thì cái gói cuối cùng còn nằm đây
 * mãi, và thẻ đứng hiện "2.31x đang bay" của một ván đã nổ từ lâu. Số thật nhưng
 * quá hạn vẫn là báo sai.
 *
 * 12 giây: rộng hơn nhịp hỏi (5s) và nhịp ghi của server (0.5s) đủ xa để mạng
 * chập chờn không làm ô hệ số nhấp nháy.
 *
 * Chỉ đo được nửa SAU của tuổi gói — phần trôi kể từ lúc NHẬN. Nửa trước (gói nằm
 * trong Redis / trên đường tới) là chốt khác lo: `isPacketStaleOnArrival`.
 */
const MAX_PACKET_AGE_MS = 12_000;

const PHASE_LABELS: Record<GameRoundPhase, string> = {
  [GameRoundPhase.WAITING]: 'CHỜ VÁN',
  [GameRoundPhase.BETTING]: 'ĐANG CƯỢC',
  [GameRoundPhase.ROLLING]: 'ĐANG QUAY',
};

const PHASE_COLORS: Record<GameRoundPhase, string> = {
  [GameRoundPhase.WAITING]: 'text-ink-dim bg-ink-dim/10',
  [GameRoundPhase.BETTING]: 'text-success bg-success/10',
  [GameRoundPhase.ROLLING]: 'text-warning bg-warning/10',
};

/** gid của observer: 1 TX Live · 2 XĐ Live · 3 XĐ MD5 · 4 Máy bay · 5 Bầu Cua. */
const GAME_ICONS: Record<number, typeof Dices> = { 1: Dices, 2: Dices, 3: Dices, 4: Plane, 5: Dices };

interface GameRoundCardsProps {
  games: IGameRoundState[];
  /**
   * Mốc (giờ CỦA TRÌNH DUYỆT) lúc nhận gói `games` hiện tại.
   *
   * Còn dùng để đo TUỔI của gói (xem `MAX_PACKET_AGE_MS`), chứ không còn để suy
   * ra hệ số nữa. Đo bằng giờ trình duyệt cho cả hai đầu nên kết quả không phụ
   * thuộc đồng hồ máy chủ có khớp máy người dùng hay không.
   *
   * Với hệ số Máy bay thì đây chỉ là MỘT NỬA tuổi — nửa "trôi sau khi nhận". Nửa
   * kia (`oddAgeMs`) server đo và gửi kèm, vì mốc nhận GÓI không phải mốc của nhịp
   * hệ số; xem `isOddFresh`.
   */
  receivedAt: number;
  /**
   * Gói này đã CŨ SẴN từ lúc tới nơi chưa (xem `isPacketStaleOnArrival`).
   *
   * Trường thứ ba của cùng một câu hỏi tuổi, và là trường DUY NHẤT nhìn thấy được
   * khúc "nằm trong Redis / đi trên đường": `receivedAt` chỉ đếm từ lúc nhận, còn
   * `oddAgeMs` dừng ở lúc server ghi. Cũ sẵn lúc tới nơi = mọi con số SỐNG trong
   * gói đều không chứng minh được là còn đúng.
   */
  staleOnArrival: boolean;
}

function RoundCard({
  game,
  now,
  receivedAt,
  staleOnArrival,
}: {
  game: IGameRoundState;
  now: number;
  receivedAt: number;
  staleOnArrival: boolean;
}) {
  const Icon = GAME_ICONS[game.gid] ?? Dices;

  const remainingMs = game.betDeadlineAt === null ? null : Math.max(0, game.betDeadlineAt - now);
  // Máy bay đang bay: hệ số mới là con số đáng nhìn, không phải đồng hồ đã về 0.
  //
  // Hệ số hiển thị là `game.odd` THÔ — hệ số nhà cái vừa gửi, không nhân thêm gì.
  // Bản trước tự bồi tiếp `e^(6e-5·Δt)` cho phần trôi từ lúc nhận gói để chữ số
  // chạy mượt 60ms một lần; đổi lại, thứ trên màn hình là số web tự bịa và nó
  // luôn chạy TRƯỚC số thật.
  //
  // Hệ quả có chủ ý: số bây giờ NHẢY theo nhịp nhà cái (~2-3 lần/giây) chứ không
  // trôi mượt nữa. Chấp nhận — thà nhảy mà đúng còn hơn mượt mà sai, vì con số
  // này là thứ khách nhìn để quyết định thoát lệnh.
  const sinceReceived = Math.max(0, now - receivedAt);
  // `game.flying` là cờ server gửi kèm suốt chuyến bay, cộng HAI chốt tuổi cho hai
  // khúc khác nhau: `staleOnArrival` chặn gói đã cũ TRƯỚC khi tới nơi (đọc trúng
  // gói cached của một observer đã chết), `MAX_PACKET_AGE_MS` chặn gói cũ đi SAU
  // khi tới nơi (đường đẩy chết, tab ngủ). Chốt đầu bao luôn cả ô hệ số bên dưới:
  // `game.odd` chỉ được vẽ trong nhánh `flying` này.
  const flying =
    game.flying === true && !staleOnArrival && sinceReceived <= MAX_PACKET_AGE_MS;
  // Còn CON SỐ thì soi bằng chốt chặt hơn nhiều (xem `isOddFresh`): "đang bay" trễ
  // vài giây chỉ là hơi cũ, còn hệ số trễ vài giây là sai hẳn một mức. Và tuổi của
  // nó tính từ NHỊP HỆ SỐ (`oddAgeMs`, server đo) chứ không từ mốc nhận gói — ba
  // game kia chuyển pha là gói mới được ghi với `odd` Máy bay y nguyên.
  const oddFresh = isOddFresh(game.oddAgeMs, sinceReceived);
  // Tự hạ trạng thái khi đồng hồ chạy hết giữa hai lần hỏi — nếu không thì thẻ
  // vẫn ghi "ĐANG CƯỢC · 0s" tới tận nhịp sau.
  const phase =
    remainingMs === 0 && game.phase === GameRoundPhase.BETTING
      ? GameRoundPhase.ROLLING
      : game.phase;

  return (
    <div className="border-line bg-panel flex flex-col gap-2.5 rounded-xl border p-3.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Icon className="text-accent h-3.5 w-3.5 shrink-0" />
          <span className="text-ink truncate text-[13px] font-bold">{game.name}</span>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${PHASE_COLORS[phase]}`}
        >
          {PHASE_LABELS[phase]}
        </span>
      </div>

      {/* Ô số lớn = thứ đang NHẢY của game đó: Máy bay đang bay → hệ số · có đếm
          ngược → đồng hồ (tiền tố "~" nếu là số tự đo) · Máy bay vừa nổ, chưa mở
          ván mới → hệ số nổ. */}
      <div className="flex items-baseline gap-1.5">
        {flying ? (
          <>
            <Rocket className="text-warning h-3.5 w-3.5 shrink-0 self-center" />
            {/* Gạch ngang trong HAI trường hợp: vừa cất cánh mà nhà cái chưa gửi
                nhịp hệ số nào (khoảng này tới ~2.6 giây — nhịp thưa nhất đo được
                là 2593ms), hoặc gói đã quá hạn nên con số không còn đúng nữa. Tự
                vẽ một con số cho đỡ trống là quay lại đúng cái sai vừa bỏ. */}
            <span className="text-warning font-mono text-[22px] font-bold leading-none">
              {game.odd === undefined || !oddFresh ? '—' : `${game.odd.toFixed(2)}x`}
            </span>
          </>
        ) : remainingMs !== null ? (
          <>
            <Timer className="text-ink-faint h-3.5 w-3.5 shrink-0 self-center" />
            <span
              // Xanh = CÒN ĐẶT ĐƯỢC. Soi theo `phase` chứ không theo `remainingMs > 0`
              // nữa: Xóc Đĩa MD5 lúc ĐANG QUAY cũng có đồng hồ (đếm tới ván mới),
              // và tô xanh nó là mời khách đặt vào một cửa đã đóng. `phase` ở trên
              // đã tự hạ xuống ROLLING khi đồng hồ về 0 nên không cần kiểm thêm.
              className={`font-mono text-[22px] font-bold leading-none ${
                phase === GameRoundPhase.BETTING ? 'text-success' : 'text-ink-dim'
              }`}
            >
              {/* Tiền tố "~" = đồng hồ này là số TỰ ĐO, không phải số nhà cái gửi
                  (xem `deadlineEstimated`). Chỉ Xóc Đĩa MD5 rơi vào ca đó, và chỉ
                  khi không có `rmT` thật từ snapshot. Một ký tự là đủ để phân
                  biệt — nhét thêm nhãn vào thẻ chỉ làm loãng ô số lớn. */}
              {`${game.deadlineEstimated === true ? '~' : ''}${String(Math.ceil(remainingMs / 1000))}s`}
            </span>
          </>
        ) : game.detail ? (
          // Ván Máy bay vừa nổ, chưa mở ván mới: hệ số nổ mới là thứ đáng đọc,
          // không phải một gạch ngang. Làm mờ để không lẫn với hệ số đang bay.
          <>
            <Rocket className="text-ink-faint h-3.5 w-3.5 shrink-0 self-center" />
            <span className="text-ink-dim font-mono text-[22px] font-bold leading-none">
              {game.detail}
            </span>
          </>
        ) : (
          <>
            <Timer className="text-ink-faint h-3.5 w-3.5 shrink-0 self-center" />
            <span className="text-ink-dim font-mono text-[22px] font-bold leading-none">—</span>
          </>
        )}

        {/* Kết quả ván trước bám theo tới tận lúc ván mới CẤT CÁNH: lúc đang đếm
            ngược để cược, ô lớn là đồng hồ nên nó lùi sang đây. Server xoá `detail`
            đúng ở `takeoff`, nên nó tự biến mất khi chuyến bay mới bắt đầu. */}
        {game.detail && (flying || remainingMs !== null) ? (
          <span className="text-ink-faint ml-auto font-mono text-[13px] font-bold">
            {game.detail}
          </span>
        ) : null}
      </div>

      <div className="border-line flex items-center justify-between gap-2 border-t pt-2 text-[11px]">
        <span className="text-ink-dim flex min-w-0 items-center gap-1 font-mono">
          <Hash className="h-3 w-3 shrink-0" />
          {/* MD5 đánh số ván bằng chuỗi commit MD5, không phải số nguyên — server
              đã quy về `sessionLabel` để chỗ này không phải biết game nào là game nào. */}
          <span className="truncate">{game.sessionLabel ?? '—'}</span>
        </span>
        <span className="text-warning shrink-0 font-mono font-bold">
          {game.jackpot === null ? '—' : formatMoney(game.jackpot)}
        </span>
      </div>
    </div>
  );
}

/**
 * Bốn thẻ ván — một thẻ mỗi game, dựng từ TÀI KHOẢN BÁO HŨ.
 *
 * Thay cho dải thống kê tài khoản cũ. Khác thanh phiên của nhóm ở chỗ căn bản:
 * cái kia chỉ có khi nhóm đang chạy, còn cái này là ván của CHÍNH GAME nên luôn
 * đúng — kể cả khi người dùng chưa bật gì. Nguồn là observer hũ ở worker, vốn đã
 * nối sẵn cả 4 game, nên không tốn thêm socket nào.
 *
 * Rỗng = observer chưa cấu hình hoặc đã chết → ẩn hẳn, không vẽ 4 ô trống.
 */
export function GameRoundCards({ games, receivedAt, staleOnArrival }: GameRoundCardsProps) {
  const [now, setNow] = useState(() => Date.now());

  // Chạy đồng hồ khi có đếm ngược HOẶC có máy bay đang bay. Vế sau KHÔNG còn để
  // tính hệ số (hệ số giờ lấy thẳng từ gói), mà để hai chốt tuổi gói
  // (`MAX_ODD_AGE_MS`, `MAX_PACKET_AGE_MS`) có cơ hội hết hạn: thiếu nó thì gói
  // cuối cùng của một observer đã chết sẽ treo mãi trên thẻ vì không còn lần vẽ
  // lại nào.
  const ticking = games.some((game) => game.betDeadlineAt !== null || game.flying === true);
  useEffect(() => {
    if (!ticking) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [ticking]);

  if (games.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
      {games.map((game) => (
        <RoundCard
          key={game.gid}
          game={game}
          now={now}
          receivedAt={receivedAt}
          staleOnArrival={staleOnArrival}
        />
      ))}
    </div>
  );
}

/** Khung xám lúc đang tải — giữ đúng chiều cao dải thẻ để trang khỏi giật. */
export function LobbySkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {[0, 1, 2, 3, 4].map((key) => (
          <div
            key={key}
            className="border-line bg-panel h-[116px] animate-pulse rounded-xl border"
          />
        ))}
      </div>
      <div className="border-line bg-panel h-64 animate-pulse rounded-xl border" />
    </div>
  );
}
